<template>
  <teleport to="body">
    <div
      v-if="floatingPopup.isVisible"
      class="floating-popup"
      :class="{ 'floating-popup--minimized': floatingPopup.isMinimized }"
      :style="popupStyle"
    >
      <!-- Header -->
      <div
        class="bg-[#073763] text-white p-3 rounded-t-lg cursor-move select-none flex items-center justify-between"
        @mousedown="startDrag"
      >
        <div class="flex items-center font-semibold text-base">
          <el-icon class="mr-2">
            <fa-icon v-if="floatingPopup.type === 'task'" :icon="faClipboardCheck" class="text-[18px]" />
            <fa-icon v-else-if="floatingPopup.type === 'interaction'" :icon="faPhone" class="text-[18px]" />
          </el-icon>
          <span>{{ popupTitle }}</span>
        </div>
        <div class="flex gap-1">
          <el-button text size="small" class="popup-control-btn" @click="toggleMinimize">
            <el-icon>
              <component :is="floatingPopup.isMinimized ? 'Plus' : 'Minus'" />
            </el-icon>
          </el-button>

          <el-button text size="small" class="popup-control-btn" @click="closePopup">
            <el-icon>
              <Close />
            </el-icon>
          </el-button>
        </div>
      </div>

      <!-- Content -->
      <div v-show="!floatingPopup.isMinimized" class="flex-1 overflow-y-auto overflow-x-hidden p-5 relative">
        <component
          :is="currentComponent"
          v-if="currentComponent"
          :member-id="floatingPopup.memberId"
          :opportunity-id="floatingPopup.opportunityId"
          :task-id="floatingPopup.taskId"
          :interaction-id="floatingPopup.interactionId"
          @close="closePopup"
          @save="handleSave"
        />
      </div>
    </div>
  </teleport>
</template>

<script>
import { Close, Minus, Plus } from '@element-plus/icons-vue'
import { faClipboardCheck, faPhone } from '@fortawesome/free-solid-svg-icons'
import { computed, ref, onUnmounted, defineAsyncComponent } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'FloatingPopup',
  components: {
    Close,
    Minus,
    Plus,
  },
  setup() {
    const store = useStore()
    const isDragging = ref(false)
    const dragOffset = ref({ x: 0, y: 0 })

    const floatingPopup = computed(() => store.state.globalComponents.floatingPopup)

    const popupTitle = computed(() => {
      const type = floatingPopup.value.type
      if (type === 'task') {
        return floatingPopup.value.taskId ? 'Edit Task' : 'New Task'
      }
      if (type === 'interaction') {
        return floatingPopup.value.interactionId ? 'Edit Interaction' : 'New Interaction'
      }
      return 'Form'
    })

    const popupStyle = computed(() => {
      const position = floatingPopup.value.position
      return {
        bottom: `${position.bottom}px`,
        right: `${position.right}px`,
      }
    })

    const currentComponent = computed(() => {
      const type = floatingPopup.value.type
      if (type === 'task') {
        return defineAsyncComponent(() => import('./FloatingTaskForm.vue'))
      }
      if (type === 'interaction') {
        return defineAsyncComponent(() => import('./FloatingInteractionForm.vue'))
      }
      // Add other component types here as needed
      return null
    })

    const toggleMinimize = () => {
      if (floatingPopup.value.isMinimized) {
        store.commit('globalComponents/maximizeFloatingPopup')
      } else {
        store.commit('globalComponents/minimizeFloatingPopup')
      }
    }

    const closePopup = () => {
      store.commit('globalComponents/closeFloatingPopup')
    }

    const handleSave = () => {
      // Optional: handle save event
      closePopup()
    }

    // Drag functionality
    const startDrag = (e) => {
      isDragging.value = true
      dragOffset.value = {
        x: e.clientX,
        y: e.clientY,
      }
      document.addEventListener('mousemove', onDrag)
      document.addEventListener('mouseup', stopDrag)
    }

    const onDrag = (e) => {
      if (!isDragging.value) {
        return
      }

      const deltaX = dragOffset.value.x - e.clientX
      const deltaY = dragOffset.value.y - e.clientY

      const newPosition = {
        right: floatingPopup.value.position.right + deltaX,
        bottom: floatingPopup.value.position.bottom + deltaY,
      }

      // Bounds checking
      const minRight = 0
      const minBottom = 0
      const maxRight = window.innerWidth - 400
      const maxBottom = window.innerHeight - 100

      newPosition.right = Math.max(minRight, Math.min(maxRight, newPosition.right))
      newPosition.bottom = Math.max(minBottom, Math.min(maxBottom, newPosition.bottom))

      store.commit('globalComponents/updateFloatingPopupPosition', newPosition)

      dragOffset.value = {
        x: e.clientX,
        y: e.clientY,
      }
    }

    const stopDrag = () => {
      isDragging.value = false
      document.removeEventListener('mousemove', onDrag)
      document.removeEventListener('mouseup', stopDrag)
    }

    onUnmounted(() => {
      document.removeEventListener('mousemove', onDrag)
      document.removeEventListener('mouseup', stopDrag)
    })

    return {
      floatingPopup,
      popupTitle,
      popupStyle,
      currentComponent,
      toggleMinimize,
      closePopup,
      handleSave,
      startDrag,
      faClipboardCheck,
      faPhone,
    }
  },
}
</script>

<style lang="scss" scoped>
.floating-popup {
  position: fixed;
  width: 400px;
  max-width: calc(100vw - 40px);
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 100px);

  &--minimized {
    width: 350px;
    max-height: 60px;
  }
}

// Ensure Element Plus dropdowns appear above the floating popup
:deep(.el-select-dropdown),
:deep(.el-picker-panel),
:deep(.el-autocomplete-suggestion),
:deep(.el-popper) {
  z-index: 2100 !important;
}
.popup-control-btn {
  border-radius: 6px;
}

.popup-control-btn .el-icon {
  color: white;
  font-size: 18px;
  transition: color 0.2s ease;
}

.popup-control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.popup-control-btn:hover .el-icon {
  color: black;
}
</style>
